<template>
<v-dialog :value="value" max-width="640" scrollable @input="$emit('input', $event)" @keydown.esc="close">
  <v-card class="rounded-xl" style="overflow: hidden;">

    <!-- Header -->
    <div class="acm-header">
      <div>
        <h3 class="acm-title">Добавить компетенцию</h3>
        <p class="acm-subtitle">Компетенция будет добавлена в чек-лист вместе с её индикаторами из библиотеки.</p>
      </div>
      <v-btn icon small @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Body -->
    <v-card-text class="pa-0" style="overflow: hidden;">
      <div class="acm-search">
        <v-text-field
          v-model="search"
          dense
          outlined
          hide-details
          autofocus
          placeholder="Поиск компетенций"
          prepend-inner-icon="mdi-magnify"
        />
      </div>

      <div class="acm-list">
        <template v-if="loading">
          <v-skeleton-loader type="list-item-two-line" class="mb-2"/>
          <v-skeleton-loader type="list-item-two-line" class="mb-2"/>
          <v-skeleton-loader type="list-item-two-line"/>
        </template>

        <template v-else-if="filteredCompetencies.length">
          <div
            v-for="comp in filteredCompetencies"
            :key="comp.id"
            class="acm-item"
          >
            <div class="acm-item__body">
              <div class="d-flex align-center flex-wrap" style="gap:6px;">
                <span class="acm-item__name">{{ comp.name }}</span>
                <v-chip
                  v-if="comp.category"
                  label x-small
                  style="background:#f3f4f6; color:#6b7280; height:18px; font-size:10px;"
                >{{ comp.category }}</v-chip>
              </div>
              <p v-if="comp.description" class="acm-item__desc">{{ comp.description }}</p>
            </div>
            <v-btn
              v-if="!isAttached(comp)"
              depressed
              small
              outlined
              color="#212121"
              class="acm-item__btn flex-shrink-0"
              :loading="addingId === comp.id"
              :disabled="!!addingId && addingId !== comp.id"
              @click="$emit('add', comp)"
            >
              <v-icon x-small left>mdi-plus</v-icon>
              Добавить
            </v-btn>
            <div v-else class="acm-added-badge">
              <v-icon x-small color="#009E60">mdi-check</v-icon>
              Добавлена
            </div>
          </div>
        </template>

        <div v-else class="acm-empty">
          <template v-if="search">Ничего не найдено по запросу «{{ search }}».</template>
          <template v-else>Библиотека компетенций пуста.</template>
        </div>
      </div>
    </v-card-text>

    <!-- Footer -->
    <v-divider/>
    <div class="acm-footer">
      <v-btn outlined color="grey darken-1" height="40" style="min-width:100px;" @click="close">
        Закрыть
      </v-btn>
    </div>

  </v-card>
</v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AddCompetenceModal',

  props: {
    value: { type: Boolean, default: false },
    competencies: { type: Array, default: () => [] },
    addingId: { type: [String, Number], default: null },
  },

  data: () => ({
    search: '',
    loading: false,
  }),

  computed: {
    ...mapGetters({
      libraryCompetencies: 'diagnostics/diagnotics/libraryCompetencies',
    }),

    attachedTemplateIds() {
      return new Set(
        this.competencies.map(c => c.source_competence_template).filter(Boolean),
      );
    },

    filteredCompetencies() {
      if (!this.search.trim()) return this.libraryCompetencies;
      const q = this.search.trim().toLowerCase();
      return this.libraryCompetencies.filter(c => c.name.toLowerCase().includes(q)
        || (c.description && c.description.toLowerCase().includes(q)));
    },
  },

  watch: {
    async value(open) {
      if (!open) return;
      this.search = '';
      if (this.libraryCompetencies.length) return;
      this.loading = true;
      try {
        await this.loadCompetenciesLibrary();
      } finally {
        this.loading = false;
      }
    },
  },

  methods: {
    ...mapActions({
      loadCompetenciesLibrary: 'diagnostics/diagnotics/loadCompetenciesLibrary',
    }),

    isAttached(comp) {
      return this.attachedTemplateIds.has(comp.id);
    },

    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
.acm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 12px;
}
.acm-title {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
}
.acm-subtitle {
  font-size: 13px;
  color: var(--v-primary-base, #009E60);
  margin: 0;
}

.acm-search {
  padding: 0 24px 12px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 16px;
}

.acm-list {
  height: 420px;
  overflow-y: auto;
  padding: 12px 24px 4px;
}

.acm-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: #fff;
}
.acm-item__body { flex: 1; min-width: 0; }
.acm-item__name {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
}
.acm-item__desc {
  font-size: 12px;
  color: #757575;
  margin: 2px 0 0;
}
.acm-item__btn {
  font-size: 12px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.acm-added-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #009E60;
  font-weight: 500;
  flex-shrink: 0;
}

.acm-empty {
  text-align: center;
  font-size: 13px;
  color: #bdbdbd;
  padding: 40px 20px;
}

.acm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
}
</style>
